import {
  Button,
  Input,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const STATUS_OPTIONS = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "confirmed", label: "ຢືນຢັນ" },
  { value: "paid", label: "ຊຳລະແລ້ວ" },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export type PayrollListSearchParams = OffsetPageQueryDTO & {
  periodYear?: number;
  periodMonth?: number;
};

export function PayrollFilter() {
  const nav = useNavigate({ from: "/app/hr/payroll" });
  const search = useSearch({ from: "/app/hr/payroll" }) as PayrollListSearchParams;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const statusFilter = findCondition(filters, "status");
  const nameFilter = findCondition(filters, "name");
  const emailFilter = findCondition(filters, "email");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || (emailFilter?.value as string) || "",
  );

  useEffect(() => {
    const val = (nameFilter?.value as string) || (emailFilter?.value as string) || "";
    setSearchValue(val);
  }, [nameFilter?.value, emailFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "name", op: "contains", value: val },
        { field: "email", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "name");
      nextFilters = removeConditions(nextFilters, "email");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        periodYear: search.periodYear ?? currentYear,
        periodMonth: search.periodMonth ?? currentMonth,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    statusFilter?.value != null ? String(statusFilter.value) : "all";
  const hasFilter =
    !!statusFilter?.value ||
    !!nameFilter?.value ||
    !!emailFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາພະນັກງານ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
            aria-label="ຄົ້ນຫາຕາມຊື່ ຫຼື ອີເມວພະນັກງານ"
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  periodYear: search.periodYear ?? currentYear,
                  periodMonth: search.periodMonth ?? currentMonth,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={STATUS_OPTIONS}
            aria-label="ກັ່ນຕອງຕາມສະຖານະ"
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  filters: undefined,
                  offset: 0,
                  periodYear: search.periodYear ?? currentYear,
                  periodMonth: search.periodMonth ?? currentMonth,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
